import React, { useState, useCallback, useEffect  } from 'react';
import type {
  PlanSelection,
  TravelFormData,
  LandingSectionProps,
  EnhancedLandingSectionProps,
  LandingFormDataCapture
} from '../../types/customer-portal';
import TravelInformationSection from './TravelInformationSection';
import pcphLandingImage from '../../../../assets/Images/pcph-landing-image.png';
import Covid19TermsModal from './Covid19TermsModal';

import './LandingSection.css';

interface PlanCardData {
  title: string;
  subtitle: string;
  features: string[];
  planType: PlanSelection['planType'];
  section: PlanSelection['section'];
}

// NEW: Enhanced component that supports both original and enhanced props
const LandingSection: React.FC<LandingSectionProps & Partial<EnhancedLandingSectionProps>> = ({
  onPurchaseClick,
  onShowPlans,
  // NEW: Optional enhanced props (Phase 2 - Form Data Capture)
  captureFormData = false,
  onPurchaseWithFormData,
  // NEW: Optional callback for SEE MY QUOTE button to route directly to TravelDetailsSection
  onSeeMyQuote
}) => {
  const [selectedPlan, setSelectedPlan] = useState<PlanSelection | undefined>(undefined);
  
  // NEW: State to track the latest form data from integrated form (Phase 2 - Form Data Capture)
  const [latestFormData, setLatestFormData] = useState<TravelFormData | null>(null);
  
  // COVID-19 Terms Modal state
  const [showCovid19Modal, setShowCovid19Modal] = useState(false);
  
  // NEW: Callback to receive form data updates from integrated TravelInformationSection
  const handleFormDataUpdate = useCallback((formData: TravelFormData) => {
    if (captureFormData) {
      setLatestFormData(formData);
      console.log('LandingSection: Form data updated:', formData);
    }
  }, [captureFormData]);

    // Session-based modal display logic
  useEffect(() => {
    const COVID19_MODAL_SESSION_KEY = 'covid19_terms_modal_shown';
    const hasShownModal = sessionStorage.getItem(COVID19_MODAL_SESSION_KEY);
    
    if (!hasShownModal) {
      // Show modal after a brief delay to ensure the page has loaded
      const timer = setTimeout(() => {
        setShowCovid19Modal(true);
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, []);

  // COVID-19 Modal handlers
  const handleAcknowledgeCovid19Terms = useCallback(() => {
    setShowCovid19Modal(false);
    // Mark as shown in session storage
    sessionStorage.setItem('covid19_terms_modal_shown', 'true');
  }, []);


  const planData: PlanCardData[] = [
    {
      title: 'Worldwide Elite',
      subtitle: 'Euro & Dollar Plans for worldwide travel',
      features: [
        'Travelsafe Insurance Plans for European and U.S. trips, including Schengen visa-approved coverage',
        'Medical Treatment Benefit of up to USD50,000 or EUR60,000',
        'Online travel insurance available anytime, anywhere',
        'Coverage for a wide array of travel inconveniences, including 24/7 worldwide emergency assistance benefits'
      ],
      planType: 'Individual Plan',
      section: 'WorldwideElite'
    },
    {
      title: 'Asia and Oceania',
      subtitle: 'Dollar & Peso Plans for Asia & Oceania',
      features: [
        'Travelsafe Insurance Plans for destinations within Asia and Oceania, Australia, New Zealand, Maldives, Hong Kong, Japan, Korea, Singapore, Thailand, etc.',
        'Medical Treatment Benefit of up to USD50,000 or PHP1,500,000',
        'Online travel insurance available anytime, anywhere',
        'Coverage for a wide array of travel inconveniences, including 24/7 worldwide emergency assistance benefits'
      ],
      planType: 'Individual Plan',
      section: 'Asia and Oceania'
    },
    {
      title: 'Domestic',
      subtitle: 'Peso Plans for trips within Philippines',
      features: [
        'Travelsafe Insurance Plans for destinations across the Philippines',
        'Medical Treatment Benefit of up to PHP 2,500,000',
        'Online travel insurance available anytime, anywhere',
        'Coverage including 24/7 emergency assistance benefits'
      ],
      planType: 'Individual Plan',
      section: 'Domestic'
    },
    {
      title: 'ShortSecure',
      subtitle: 'Travel Plans for Land and Sea Travel',
      features: [
        'Travelsafe Insurance Plans for destinations across the Philippines which can be reached via land or sea only',
        'Medical Treatment Benefit of up to PHP500,000',
        'Online travel insurance available anytime, anywhere',
        'Coverage is allowed for travel using rented, public and company vehicles as well as private cars even when the Insured Person is driving'
      ],
      planType: 'Individual Plan',
      section: 'ShortSecure'
    }
  ];

  const handlePlanClick = (planType: PlanSelection['planType'], section: PlanSelection['section']) => {
    const planSelection: PlanSelection = { planType, section };
    setSelectedPlan(planSelection);
    
    // EXISTING: Original callback (unchanged for backward compatibility)
    if (onPurchaseClick) {
      onPurchaseClick(planType, section);
    }
    
    // NEW: Optional form data capture and enhanced callback (Phase 2)
    if (captureFormData && onPurchaseWithFormData && latestFormData) {
      try {
        // Calculate completion status
        const totalFields = 8; // Approximate number of key fields
        let filledFields = 0;
        
        if (latestFormData.tripType) filledFields++;
        if (latestFormData.planType) filledFields++;
        if (latestFormData.startTrip) filledFields++;
        if (latestFormData.destination.length > 0) filledFields++;
        if (latestFormData.endTrip) filledFields++;
        if (latestFormData.purposeOfTravel) filledFields++;
        if (latestFormData.startDate) filledFields++;
        if (latestFormData.endDate || latestFormData.travelDate) filledFields++;
        
        const completionPercentage = Math.round((filledFields / totalFields) * 100);
        
        // Convert TravelFormData to LandingFormDataCapture
        const capturedData: LandingFormDataCapture = {
          tripCategory: latestFormData.tripCategory,
          tripType: latestFormData.tripType,
          planType: latestFormData.planType || undefined, // Handle empty string
          startTrip: latestFormData.startTrip || undefined,
          destination: latestFormData.destination.length > 0 ? latestFormData.destination : undefined,
          endTrip: latestFormData.endTrip || undefined,
          purposeOfTravel: latestFormData.purposeOfTravel || undefined,
          startDate: latestFormData.startDate || undefined,
          endDate: latestFormData.endDate || undefined,
          travelDate: latestFormData.travelDate || undefined,
          philippinesEndpoint: latestFormData.philippinesEndpoint,
          transportMode: latestFormData.transportMode,
          homeCountry: latestFormData.homeCountry || undefined,
          // Metadata
          capturedAt: new Date().toISOString(),
          isPartialData: completionPercentage < 100
        };
        
        console.log('LandingSection: Captured form data for Buy Now:', {
          planType,
          section,
          capturedData,
          completionPercentage: `${completionPercentage}%`
        });
        
        onPurchaseWithFormData(planType, section, capturedData);
      } catch (error) {
        console.warn('LandingSection: Failed to capture form data, falling back to original callback:', error);
        // Fallback to original callback if form capture fails
        if (onPurchaseClick) {
          onPurchaseClick(planType, section);
        }
      }
    } else if (captureFormData && onPurchaseWithFormData) {
      // If form capture is enabled but no form data is available, still call enhanced callback with empty data
      console.log('LandingSection: Form capture enabled but no form data available, calling enhanced callback with empty data');
      onPurchaseWithFormData(planType, section, {
        capturedAt: new Date().toISOString(),
        isPartialData: true
      });
    }
  };

  const handleFormShowPlans = (formData: TravelFormData, planSelection?: PlanSelection) => {
    if (onShowPlans) {
      onShowPlans(formData, planSelection);
    }
  };

  // NEW: Handler for SEE MY QUOTE button in integrated form - routes directly to TravelDetailsSection
  const handleFormSeeMyQuote = (formData: TravelFormData, planSelection?: PlanSelection) => {
    if (onSeeMyQuote) {
      onSeeMyQuote(formData, planSelection);
    }
  };

  // NEW: Handler for BUY NOW button in integrated form
  const handleFormBuyNow = (formData: TravelFormData, planSelection?: PlanSelection) => {
    console.log('LandingSection: BUY NOW clicked with form data:', formData);
    console.log('LandingSection: Plan selection provided:', planSelection);
    console.log('LandingSection: Current latestFormData state:', latestFormData);
    console.log('LandingSection: captureFormData flag:', captureFormData);
    console.log('LandingSection: onPurchaseWithFormData callback available:', !!onPurchaseWithFormData);
    
    // Create a plan selection if none provided, based on form data and trip category
    let effectivePlanSelection: PlanSelection;
    
    if (planSelection) {
      effectivePlanSelection = planSelection;
    } else {
      // Create default plan selection based on form data
      let defaultPlanType: PlanSelection['planType'] = 'Individual Plan';
      let defaultSection: PlanSelection['section'] = 'Domestic';
      
      // Determine plan type from form data
      if (formData.tripType === 'Annual Multi-Trip') {
        defaultPlanType = 'Multi Trip';
      } else if (formData.planType === 'Family') {
        defaultPlanType = 'Family Plan';
      } else if (formData.planType === 'Group') {
        defaultPlanType = 'Group Plan';
      } else {
        defaultPlanType = 'Individual Plan';
      }
      
      // Determine section based on trip category and transport mode
      if (formData.tripCategory === 'International') {
        // CRITICAL FIX: For International multi-trip, use undefined section
        // This allows TravelDetailsSection to fetch both Asia Oceania and WorldwideElite plans
        if (formData.tripType === 'Annual Multi-Trip') {
          defaultSection = undefined; // Let TravelDetailsSection handle international multi-trip
        } else {
          defaultSection = 'WorldwideElite'; // Only for single trip international
        }
      } else if (formData.tripCategory === 'Domestic') {
        if (formData.transportMode === 'land' || formData.transportMode === 'sea') {
          defaultSection = 'ShortSecure';
        } else {
          defaultSection = 'Domestic';
        }
      } else if (formData.tripCategory === 'Inbound') {
        defaultSection = 'WorldwideElite'; // Inbound uses international sections
      } else {
        defaultSection = 'Domestic'; // Default fallback
      }
      
      effectivePlanSelection = {
        planType: defaultPlanType,
        section: defaultSection
      };
      
      console.log('LandingSection: Created default plan selection:', effectivePlanSelection);
    }

    const { planType, section } = effectivePlanSelection;
    
    // NEW: Enhanced callback with form data (Phase 2) - PRIORITIZE THIS OVER OLD CALLBACK
    if (onPurchaseWithFormData) {
      try {
        // Calculate completion status
        const totalFields = 8; // Approximate number of key fields
        let filledFields = 0;
        
        if (formData.tripType) filledFields++;
        if (formData.planType) filledFields++;
        if (formData.startTrip) filledFields++;
        if (formData.destination.length > 0) filledFields++;
        if (formData.endTrip) filledFields++;
        if (formData.purposeOfTravel) filledFields++;
        if (formData.startDate) filledFields++;
        if (formData.endDate || formData.travelDate) filledFields++;
        
        const completionPercentage = Math.round((filledFields / totalFields) * 100);
        
        // Convert TravelFormData to LandingFormDataCapture
        const capturedData: LandingFormDataCapture = {
          tripCategory: formData.tripCategory,
          tripType: formData.tripType,
          planType: formData.planType || undefined, // Handle empty string
          startTrip: formData.startTrip || undefined,
          destination: formData.destination.length > 0 ? formData.destination : undefined,
          endTrip: formData.endTrip || undefined,
          purposeOfTravel: formData.purposeOfTravel || undefined,
          startDate: formData.startDate || undefined,
          endDate: formData.endDate || undefined,
          travelDate: formData.travelDate || undefined,
          philippinesEndpoint: formData.philippinesEndpoint,
          transportMode: formData.transportMode,
          homeCountry: formData.homeCountry || undefined,
          // Metadata
          capturedAt: new Date().toISOString(),
          isPartialData: completionPercentage < 100
        };
        
        console.log('LandingSection: Integrated form BUY NOW clicked with data:', {
          planType,
          section,
          capturedData,
          completionPercentage: `${completionPercentage}%`
        });
        
        onPurchaseWithFormData(planType, section, capturedData);
      } catch (error) {
        console.warn('LandingSection: Failed to capture integrated form data, falling back to original callback:', error);
        // Fallback to original callback if form capture fails
        if (onPurchaseClick) {
          onPurchaseClick(planType, section);
        }
      }
    } else if (onPurchaseClick) {
      // FALLBACK: Only call original callback if enhanced callback is not available
      console.log('LandingSection: Enhanced callback not available, using original callback');
      onPurchaseClick(planType, section);
    }
  };

  return (
    <div className="landing-section">
      {/* Hero Section - Horizontal Layout */}
      <div className="hero-section">
        <div className="hero-container">
          {/* Left Side - Branding */}
          <div className="hero-left">
            <div className="hero-text">
              <h1>MyTravelsafe</h1>
              <p>With Pacific Cross, you can take on more adventures and less worries.</p>
            </div>
            <div className="hero-image">
              <img src={pcphLandingImage} alt="Woman with luggage" />
            </div>
          </div>
          
          {/* Right Side - Form Container */}
          <div className="hero-right">
            <div className="quote-form-container">
              {/* Integrated Travel Information Form with Compact Styling */}
              <div className="compact-travel-form">
                <TravelInformationSection
                  planSelection={selectedPlan}
                  onShowPlans={handleFormShowPlans}
                  // NEW: Pass form data update callback if capture is enabled (Phase 2)
                  onFormDataChange={captureFormData ? handleFormDataUpdate : undefined}
                  // NEW: Pass BUY NOW callback to enable the functional button
                  onBuyNow={handleFormBuyNow}
                  // NEW: Pass SEE MY QUOTE callback for direct routing to TravelDetailsSection
                  onSeeMyQuote={handleFormSeeMyQuote}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Insurance Plans Section - Separate Blue Container */}
      <div className="plans-section">
        <div className="plans-container">
          <h2>Learn More About our Travelsafe Insurance Plans</h2>
          <div className="plans-grid">
            {planData.map((plan, index) => (
              <div key={index} className={`plan-card plan-card-${index + 1}`}>
                <div className="plan-header">
                  <h3>{plan.title}</h3>
                  <div className="plan-subtitle-border">
                    <p className="plan-subtitle">{plan.subtitle}</p>
                  </div>
                </div>
                <div className="plan-content">
                  <ul className="plan-features">
                    {plan.features.map((feature, featureIndex) => (
                      <li key={featureIndex}>{feature}</li>
                    ))}
                  </ul>
                </div>
                <div className="plan-footer">
                  <button 
                    className="buy-now-btn"
                    onClick={() => handlePlanClick(plan.planType, plan.section)}
                  >
                    Buy Now
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* COVID-19 Terms Modal */}
      <Covid19TermsModal
        isOpen={showCovid19Modal}
        onAcknowledge={handleAcknowledgeCovid19Terms}
      />
    </div>
  );
};

export default LandingSection;